import { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import type { EventCategory } from "@/types";
import { Button, ModalOverlay, SecondaryButton } from "@components/atoms";
import {
  DateRangePicker,
  PriceRangeSlider,
  CategorySelector,
} from "@components/molecules";
import { cn } from "@/shared/utils";
import { createPortal } from "react-dom";

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface PriceRange {
  min: number;
  max: number;
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  dateRange: DateRange;
  onDateRangeChange: (range: DateRange) => void;
  priceRange: PriceRange;
  onPriceRangeChange: (range: PriceRange) => void;
  selectedCategories: EventCategory[];
  onCategoriesChange: (categories: EventCategory[]) => void;
  onClearAll: () => void;
}

/**
 * Filter modal organism.
 * Displays all filter options (date, price, categories) in a single modal.
 * Dimensions: 825×580px. Fondo: white, radius: 16px.
 */
export function FilterModal({
  isOpen,
  onClose,
  dateRange,
  onDateRangeChange,
  priceRange,
  onPriceRangeChange,
  selectedCategories,
  onCategoriesChange,
  onClearAll,
}: FilterModalProps) {
  const [tempDateRange, setTempDateRange] = useState(dateRange);
  const [tempPriceRange, setTempPriceRange] = useState(priceRange);
  const [tempCategories, setTempCategories] = useState(selectedCategories);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);
  const [pickerPosition, setPickerPosition] = useState({ top: 0, left: 0 });

  const handleApply = () => {
    onDateRangeChange(tempDateRange);
    onPriceRangeChange(tempPriceRange);
    onCategoriesChange(tempCategories);
    onClose();
  };

  const handleCancel = () => {
    setTempDateRange(dateRange);
    setTempPriceRange(priceRange);
    setTempCategories(selectedCategories);
    onClose();
  };

  const handleClearAll = () => {
    setTempDateRange({ start: null, end: null });
    setTempPriceRange({ min: 0, max: 300 });
    setTempCategories([]);
    onClearAll();
  };

  const handleToggleDatePicker = () => {
    if (!showDatePicker && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPickerPosition({
        top: rect.bottom + 1,
        left: rect.left,
      });
    }
    setShowDatePicker(!showDatePicker);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(e.target as Node)
      ) {
        setShowDatePicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <ModalOverlay isOpen={isOpen} onClose={handleCancel}>
      <div className="bg-white rounded-2xl w-full max-w-[825px] mx-4 max-h-[580px] overflow-y-auto p-8 relative">
        {/* Close button */}
        <button
          onClick={handleCancel}
          className="absolute top-6 right-6 p-2 hover:bg-neutral-10 rounded-full transition-colors"
        >
          <X size={24} className="text-primary-110" />
        </button>

        {/* Title */}
        <h2 className="text-primary-110 text-h2 font-semibold mb-6">Filtros</h2>

        <div className="mb-6">
          <h4 className="text-primary-110 font-semibold mb-3">Fecha</h4>
          <div ref={buttonRef}>
            <SecondaryButton
              withChevron
              isActive={showDatePicker}
              onClick={handleToggleDatePicker}
              className={cn(
                "!border-primary-110 [&_span]:text-primary-110 [&_svg]:text-primary-110",
                showDatePicker && "!bg-secondary-120",
              )}
            >
              Fecha
            </SecondaryButton>
          </div>
        </div>
        {showDatePicker &&
          createPortal(
            <div
              ref={datePickerRef}
              className="fixed z-[9999]"
              style={{ top: pickerPosition.top, left: pickerPosition.left }}
            >
              <DateRangePicker
                value={tempDateRange}
                onChange={(range) => {
                  setTempDateRange(range);
                  // Aplica directamente al hook sin necesitar "Aplicar" general
                  onDateRangeChange(range);
                }}
                onClear={() => {
                  setTempDateRange({ start: null, end: null });
                  onDateRangeChange({ start: null, end: null });
                  setShowDatePicker(false);
                }}
                onApply={() => {
                  onDateRangeChange(tempDateRange);
                  setShowDatePicker(false);
                }}
                onClose={() => setShowDatePicker(false)}
              />
            </div>,
            document.body,
          )}

        {/* Price section */}
        <div className="mb-8">
          <PriceRangeSlider
            value={tempPriceRange}
            onChange={setTempPriceRange}
            onClear={() => setTempPriceRange({ min: 0, max: 300 })}
            onApply={handleApply}
            showActions={false}
          />
        </div>

        {/* Category section */}
        <div className="mb-8">
          <CategorySelector
            value={tempCategories}
            onChange={setTempCategories}
            onClear={() => setTempCategories([])}
            onApply={handleApply}
            showActions={false}
            showTitle={false}
          />
        </div>

        {/* Action buttons */}
        <div className="flex gap-4 pt-8 border-t border-neutral-20">
          <Button
            className="flex-1 border border-primary-110 bg-white text-primary-110 hover:bg-primary-10"
            onClick={handleClearAll}
          >
            Limpiar todos
          </Button>

          <Button
            className="flex-1 border border-primary-110 bg-secondary-120 hover:bg-secondary-120/90"
            onClick={handleApply}
          >
            Aplicar
          </Button>
        </div>
      </div>
    </ModalOverlay>
  );
}
